import { Lock } from 'lucide-react';
import { useFoundingSpotsRemaining, FOUNDING_CAP } from '../../../hooks/useFoundingSpotsRemaining';
import { PILOT_ENDS, ALWAYS_FREE } from '../../../config/pricing-terms';

// Purely decorative bar heights for the blurred chart-preview behind the
// lock — this component never has real analytics data (free-tier shops
// don't fetch a summary; see ShopAnalyticsDashboard's isPaid gate), so
// there's nothing real to shape it from. Matches the mockup's own
// placeholder approach (#9c, `hint-placeholder-count="9"`).
const PREVIEW_BAR_HEIGHTS = [35, 55, 40, 70, 50, 85, 45, 60, 38];

export default function PaywallCard() {
  const { data: remaining, isLoading } = useFoundingSpotsRemaining();

  return (
    <div className="flex flex-col gap-3.5">
      {/* Blurred chart-shape preview — mockup #9c: chart shapes stay visible
          but unreadable behind the lock. */}
      <div className="bg-white rounded-[10px] p-[18px] overflow-hidden">
        <div className="flex items-end gap-1.5 h-20 blur-[3px] opacity-50">
          {PREVIEW_BAR_HEIGHTS.map((h, i) => (
            <div key={i} className="flex-1 bg-pixel-cyan rounded-t-[6px]" style={{ height: `${h}%` }} />
          ))}
        </div>
        <div className="flex flex-col gap-2.5 mt-4 blur-[3px] opacity-50">
          <div className="h-[13px] w-[70%] rounded-full bg-hairline" />
          <div className="h-[13px] w-[55%] rounded-full bg-hairline" />
          <div className="h-[13px] w-[62%] rounded-full bg-hairline" />
        </div>
      </div>

      <div className="bg-white rounded-[10px] p-[22px] text-center">
        <div className="w-14 h-14 rounded-[16px] bg-accent-alt mx-auto mb-4 flex items-center justify-center">
          <Lock className="w-6 h-6 text-primary" />
        </div>
        {/* Leads with what is free, not with what is locked. The old headline
            ("Analytics locked") opened on a refusal, which is a poor first
            thing to say to a café still deciding whether to try this at all. */}
        <h2 className="font-grotesk font-bold text-2xl text-primary mb-2.5">
          Free for founding members
        </h2>
        <p className="font-grotesk font-medium text-[13px] text-body-grey mb-2.5 max-w-xs mx-auto">
          Regulars, at-risk customers, peak hours and revenue insight — included free for founding
          members until {PILOT_ENDS}.
        </p>
        <p className="font-grotesk font-medium text-[12px] text-body-grey mb-[18px] max-w-xs mx-auto">
          {ALWAYS_FREE}
        </p>

        <div className="bg-background rounded-[10px] p-3.5 mb-[18px]">
          <p className="font-heading text-[8px] text-body-grey mb-2.5">FOUNDING SPOTS REMAINING</p>
          <p className="font-grotesk font-bold text-[30px] text-primary">
            {isLoading ? '—' : `${remaining ?? 0} / ${FOUNDING_CAP}`}
          </p>
        </div>

        <ul className="flex flex-col gap-2.5 text-left mb-5">
          <li className="font-grotesk font-medium text-[13px] text-primary">Track stamps + growth charts</li>
          <li className="font-grotesk font-medium text-[13px] text-primary">Find regulars to thank, at-risk to win back</li>
          <li className="font-grotesk font-medium text-[13px] text-primary">Send free coffees on the house</li>
        </ul>

        <a
          href="mailto:hello@bit-brew.coffee?subject=Founding Member"
          className="w-full rounded-full bg-coral text-white font-grotesk font-bold text-sm py-[15px] block"
        >
          Claim a founding spot →
        </a>
      </div>
    </div>
  );
}
